import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Link } from 'formlabs-web-components/Link';
import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { GridContainer, Grid, Cell } from 'formlabs-web-components/Grid';
import { Eyebrow, Header } from 'formlabs-web-components/Type';
import useIsMobile from '../../../../common/hooks/useIsMobile';
import './Blog.module.css';
import SocialShareButtons from './SocialShareButtons';

const createElementId = text => encodeURIComponent(text);

const interactionObserverPresent =
  typeof window !== 'undefined' &&
  ('IntersectionObserver' in window ||
    'IntersectionObserverEntry' in window ||
    (window.IntersectionObserverEntry &&
      'intersectionRatio' in window.IntersectionObserverEntry.prototype));

const BlogPost = ({
  title,
  children,
  category,
  pubDate,
  type,
  hideDate,
  url
}) => {
  const { t } = useTranslation();
  const [readTime, setReadTime] = useState(null);
  const [toc, setToc] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const isMobile = useIsMobile(960);

  // calculate read time
  useEffect(
    () => {
      const textPlugins = document.getElementsByClassName('text-plugin');
      const text = [...textPlugins].reduce(
        (result, element) => result + element.innerText,
        ''
      );
      const wordCount = text.split(' ').length;

      let minutes = Math.floor(parseFloat(wordCount / 200));
      let seconds = ((wordCount / 200).toFixed(2) + '').split('.');
      seconds = Math.floor(parseFloat(seconds[1] * 0.6).toFixed(2));

      if (seconds > 30) {
        minutes++;
      }
      if (minutes < 1) {
        minutes++;
        minutes = '< ' + minutes;
      }
      setReadTime(minutes);
    },
    [children]
  );

  // attach read progress observer
  const createObserver = () => {
    return new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0) {
          setActiveSection(entry.target.id);
        }
      });
    });
  };
  const observer = useRef(
    interactionObserverPresent && !isMobile ? createObserver() : null
  );
  // We have to act on rendered content because deep children structure does not allow for introspection
  useEffect(
    () => {
      if (isMobile) {
        return;
      }
      const content = document.getElementById('blog-content');
      const allHeaders = content.querySelectorAll('h2, h3');

      const links = [...allHeaders].map(header => {
        const text = header.innerText;

        const id = header.id ? header.id : createElementId(header.innerText);
        if (!header.id) {
          header.id = id;
        }

        if (observer.current) {
          observer.current.observe(header);
        }

        return {
          id,
          text,
          level: header.tagName.toLowerCase()
        };
      });

      setToc(links);
    },
    [children, isMobile]
  );

  const refined_url =
    url.slice(0, 6) === '/blog/' ? '/blog/' : url.slice(0, 4) + 'blog/';

  return (
    <div styleName="single-post-wrapper">
      <GridContainer>
        <Grid direction="x">
          <Cell largeSize={24}>
            <Link to={refined_url} styleName="all-posts">
              <i className="fl-arrow-left" />
              {t('All Posts')}
            </Link>
            <div styleName="post-type">
              <Eyebrow>{type}</Eyebrow>
            </div>
          </Cell>
          <Cell largeSize={18}>
            <Header level={1} displayAs={1} weight="600">
              {title}
            </Header>
          </Cell>
          <Cell largeSize={6}>
            <SocialShareButtons title={title} type={type} url={url} />
          </Cell>
        </Grid>
        {((!hideDate && pubDate) || category || type || readTime) && (
          <div styleName="byline">
            {!hideDate && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-calendar" />
                </div>
                <div styleName="label">{pubDate}</div>
              </div>
            )}
            {category && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-offer" />
                </div>
                <div styleName="label">{category}</div>
              </div>
            )}
            {type && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-folder_open" />
                </div>
                <div styleName="label">{type}</div>
              </div>
            )}
            {readTime && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-schedule" />
                </div>
                <div styleName="label">
                  {t('{{measure}} minute read', {
                    measure: readTime
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <Grid direction="x" primaryAlignment="align-left">
          {!isMobile ? (
            <Cell smallSize="6">
              <div styleName="toc">
                <p>{t('Table of contents')}</p>
                <ul>
                  {toc
                    ? toc.map(entry => (
                        <li key={entry.id}>
                          <Link
                            to={`#${entry.id}`}
                            styleName={classNames({
                              active: activeSection === entry.id,
                              sublevel: entry.level !== 'h2'
                            })}
                          >
                            {entry.text}
                          </Link>
                        </li>
                      ))
                    : null}
                </ul>
              </div>
            </Cell>
          ) : null}
          <Cell smallSize={isMobile ? 24 : 18} styleName="post-content">
            <div id="blog-content">{children}</div>
          </Cell>
        </Grid>
      </GridContainer>
    </div>
  );
};

export default DisplayComponent(BlogPost);
